import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Archive, Calendar, Download, FileJson, HardDrive, RefreshCw, RotateCcw, Search, Trash2, UserSearch } from "lucide-react";
import { toast } from "sonner";
import { apiRequest, downloadFile } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/useConfirm";

export default function AdminBackup() {
  const { user } = useAuth();
  const { confirm, ConfirmDialog } = useConfirm();
  const [deleted, setDeleted] = useState({ companies: [], risk_assessments: [] });
  const [scheduledBackups, setScheduledBackups] = useState(null);
  const [dsarWorkerId, setDsarWorkerId] = useState("");
  const [dsarUserId, setDsarUserId] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiRequest("/admin/deleted");
      setDeleted(r);
      if (user.role === "SUPER_ADMIN") {
        try {
          const b = await apiRequest("/admin/backup/list");
          setScheduledBackups(b);
        } catch { /* ignore non-SA */ }
      }
    } catch (err) {
      toast.error("Errore caricamento cestino", { description: err.message });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const exportBackup = async (companyId) => {
    setBusy(`backup-${companyId || "all"}`);
    try {
      const qs = companyId ? `?company_id=${companyId}` : "";
      await downloadFile(`/admin/backup/export${qs}`, `backup_${companyId || "all"}.zip`);
      toast.success("Backup ZIP scaricato");
    } catch (err) {
      toast.error("Backup fallito", { description: err.message });
    } finally { setBusy(""); }
  };

  const restoreCompany = async (cid) => {
    const ok = await confirm({
      title: "Ripristinare azienda?",
      description: "L'azienda verrà ripristinata e tornerà visibile nell'elenco principale.",
      confirmLabel: "Ripristina",
    });
    if (!ok) return;
    try {
      await apiRequest(`/companies/${cid}/restore`, { method: "POST" });
      toast.success("Azienda ripristinata");
      await load();
    } catch (err) { toast.error("Ripristino fallito", { description: err.message }); }
  };

  const restoreRisk = async (rid, cid) => {
    const ok = await confirm({
      title: "Ripristinare valutazione rischio?",
      description: "La valutazione tornerà attiva nel registro rischi.",
      confirmLabel: "Ripristina",
    });
    if (!ok) return;
    try {
      await apiRequest(`/companies/${cid}/risk-assessments/${rid}/restore`, { method: "POST" });
      toast.success("Valutazione rischio ripristinata");
      await load();
    } catch (err) { toast.error("Ripristino fallito", { description: err.message }); }
  };

  const runScheduledBackup = async () => {
    setBusy("scheduled");
    try {
      const r = await apiRequest("/admin/backup/run-scheduled", { method: "POST" });
      toast.success("Backup automatico eseguito", {
        description: `${r.filename} · ${Math.round(r.size / 1024)} KB · rotated: ${r.rotated_deleted}`,
      });
      await load();
    } catch (err) {
      toast.error("Backup automatico fallito", { description: err.message });
    } finally { setBusy(""); }
  };

  const exportDsarWorker = async () => {
    if (!dsarWorkerId.trim()) return;
    try {
      await downloadFile(`/admin/dsar/worker/${dsarWorkerId.trim()}`, `dsar_worker_${dsarWorkerId.slice(0, 8)}.json`);
      toast.success("DSAR lavoratore scaricato");
    } catch (err) {
      toast.error("DSAR fallito", { description: err.message });
    }
  };

  const exportDsarUser = async () => {
    if (!dsarUserId.trim()) return;
    try {
      await downloadFile(`/admin/dsar/user/${dsarUserId.trim()}`, `dsar_user_${dsarUserId.slice(0, 8)}.json`);
      toast.success("DSAR utente scaricato");
    } catch (err) {
      toast.error("DSAR fallito", { description: err.message });
    }
  };

  const triggerHealthCron = async () => {
    try {
      const r = await apiRequest("/admin/health-alerts/cron-tick", { method: "POST" });
      toast.success("Cron health alerts eseguito", {
        description: `${r.newly_triggered} nuovi · ${r.resolved} risolti · ${r.alerts_count_active} attivi totali`,
      });
    } catch (err) { toast.error("Trigger cron fallito", { description: err.message }); }
  };

  return (
    <div className="space-y-6" data-testid="admin-backup-page">
      <ConfirmDialog />
      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">Sicurezza & backup</p>
        <h2 className="mt-2 font-heading text-4xl font-bold text-slate-950" data-testid="admin-backup-title">Backup & ripristino dati</h2>
        <p className="mt-2 text-slate-600">Esporta backup ZIP completo delle tue aziende (dati, documenti metadata, audit log) per archivio offline o disaster recovery. I dati sanitari sono inclusi <strong>solo per MEDICO_COMPETENTE e SUPER_ADMIN</strong>. Aziende e valutazioni soft-deleted possono essere ripristinate da questa pagina.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={() => exportBackup(null)} disabled={busy.startsWith("backup")} className="bg-blue-900 hover:bg-blue-800" data-testid="admin-backup-export-all-button">
            <Archive className="h-4 w-4" />
            {busy === "backup-all" ? "Generazione..." : "Backup completo (tutte le aziende)"}
          </Button>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="admin-backup-refresh-button">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Aggiorna
          </Button>
          {user.role === "SUPER_ADMIN" && (
            <Button variant="outline" onClick={triggerHealthCron} data-testid="admin-cron-trigger-button">
              <RotateCcw className="h-4 w-4" /> Trigger cron health alerts
            </Button>
          )}
        </div>
      </section>

      <Card className="shadow-none" data-testid="admin-deleted-companies-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-xl flex items-center gap-2"><Trash2 className="h-5 w-5 text-rose-600" /> Aziende nel cestino</CardTitle>
            <Badge className="bg-rose-50 text-rose-700" data-testid="admin-deleted-companies-count">{deleted.companies?.length || 0}</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {!deleted.companies?.length && <p className="rounded-md border border-dashed p-4 text-center text-sm text-slate-500" data-testid="admin-deleted-companies-empty">Nessuna azienda eliminata. Tutte le aziende sono attive.</p>}
          {deleted.companies?.map((c) => (
            <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-rose-200 bg-rose-50/30 p-3" data-testid={`deleted-company-${c.id}`}>
              <div>
                <p className="font-semibold text-slate-950">{c.business_name}</p>
                <p className="text-xs text-slate-500">Eliminata il {(c.deleted_at || "").slice(0, 19).replace("T", " ")}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => exportBackup(c.id)} data-testid={`backup-company-${c.id}`}><Download className="h-3.5 w-3.5" /> Backup</Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => restoreCompany(c.id)} data-testid={`restore-company-${c.id}`}><RotateCcw className="h-3.5 w-3.5" /> Ripristina</Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="shadow-none" data-testid="admin-deleted-risks-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-xl flex items-center gap-2"><Trash2 className="h-5 w-5 text-rose-600" /> Valutazioni rischio nel cestino</CardTitle>
            <Badge className="bg-rose-50 text-rose-700" data-testid="admin-deleted-risks-count">{deleted.risk_assessments?.length || 0}</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {!deleted.risk_assessments?.length && <p className="rounded-md border border-dashed p-4 text-center text-sm text-slate-500" data-testid="admin-deleted-risks-empty">Nessuna valutazione di rischio eliminata.</p>}
          {deleted.risk_assessments?.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-rose-200 bg-rose-50/30 p-3" data-testid={`deleted-risk-${r.id}`}>
              <div>
                <p className="font-semibold text-slate-950">{r.risk_name || "Rischio senza nome"}</p>
                <p className="text-xs text-slate-500">Company {r.company_id?.slice(0, 8)} · Eliminata il {(r.deleted_at || "").slice(0, 19).replace("T", " ")}</p>
              </div>
              <div className="flex gap-2">
                <Button asChild variant="outline" size="sm" data-testid={`risk-open-company-${r.id}`}>
                  <Link to={`/aziende/${r.company_id}`}>Apri azienda</Link>
                </Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => restoreRisk(r.id, r.company_id)} data-testid={`restore-risk-${r.id}`}><RotateCcw className="h-3.5 w-3.5" /> Ripristina</Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      <Card className="shadow-none" data-testid="admin-dsar-card">
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2"><UserSearch className="h-5 w-5 text-blue-900" /> GDPR DSAR — Subject Access Request</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600">
            Esporta in JSON tutti i dati personali raccolti per un singolo soggetto (GDPR Art. 15 e Art. 20). I dati sanitari decifrati sono inclusi solo se sei <strong>MEDICO_COMPETENTE</strong> o <strong>SUPER_ADMIN</strong>.
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs">DSAR per lavoratore (worker_id)</Label>
              <div className="flex gap-2">
                <Input value={dsarWorkerId} onChange={(e) => setDsarWorkerId(e.target.value)} placeholder="UUID lavoratore" data-testid="dsar-worker-input" />
                <Button onClick={exportDsarWorker} disabled={!dsarWorkerId.trim()} className="bg-blue-900 hover:bg-blue-800" data-testid="dsar-worker-export-button">
                  <Download className="h-3.5 w-3.5" /> Esporta
                </Button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">DSAR per utente piattaforma (user_id)</Label>
              <div className="flex gap-2">
                <Input value={dsarUserId} onChange={(e) => setDsarUserId(e.target.value)} placeholder="UUID utente" data-testid="dsar-user-input" />
                <Button onClick={exportDsarUser} disabled={!dsarUserId.trim()} className="bg-blue-900 hover:bg-blue-800" data-testid="dsar-user-export-button">
                  <Download className="h-3.5 w-3.5" /> Esporta
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {user.role === "SUPER_ADMIN" && (
        <Card className="shadow-none" data-testid="admin-scheduled-backups-card">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="font-heading text-xl flex items-center gap-2"><HardDrive className="h-5 w-5 text-blue-900" /> Backup automatici schedulati</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={runScheduledBackup} disabled={busy === "scheduled"} data-testid="admin-run-scheduled-backup-button">
                  <Calendar className="h-3.5 w-3.5" /> {busy === "scheduled" ? "In corso..." : "Esegui ora"}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="mb-3 text-sm text-slate-600">
              Cron settimanale (<code>SCHEDULED_BACKUP_CRON</code> default <code>0 3 * * 0</code> — domenica alle 03:00). Backup completo di tutte le aziende salvato in <code>{scheduledBackups?.backup_dir || "/app/backups"}</code>. Retention configurabile via <code>BACKUP_AUTO_RETENTION</code> (default 12 file).
            </p>
            {!scheduledBackups || scheduledBackups.backups.length === 0 ? (
              <p className="rounded-md border border-dashed p-4 text-center text-sm text-slate-500" data-testid="admin-scheduled-backups-empty">Nessun backup automatico ancora generato.</p>
            ) : (
              <div className="overflow-hidden rounded-md border border-slate-200" data-testid="admin-scheduled-backups-list">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="px-3 py-2">File</th>
                      <th className="px-3 py-2">Generato il</th>
                      <th className="px-3 py-2">Dimensione</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scheduledBackups.backups.map((b) => (
                      <tr key={b.filename} className="border-t border-slate-100" data-testid={`scheduled-backup-${b.filename}`}>
                        <td className="px-3 py-2 font-mono text-xs">{b.filename}</td>
                        <td className="px-3 py-2 text-slate-600">{b.created_at?.slice(0, 19).replace("T", " ")}</td>
                        <td className="px-3 py-2 text-slate-600">{Math.round(b.size / 1024)} KB</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
